// The queue's words: a proposal's step, what a landing waits for, why a
// person must approve, and how an approval fared.
import { inMacShell } from '../lib/shell'
import { projectPath } from '../projects/routing'
import type { Issue, Landing, PersonReason, Proposal, ProposalStep, SubjectPart } from '../api/types'
import type { ApprovalOutcome } from '../store/store'

/** What the page says of the last approval: taken, not taken, or not sent. */
export interface ApprovalSaid {
  tone: 'ok' | 'warn' | 'error'
  text: string
}

/** What the approval strip says of an approval's result. */
export function approvalSaid(outcome: ApprovalOutcome): ApprovalSaid {
  if (!outcome.ok) return { tone: 'error', text: problemText(outcome) }
  if (outcome.accepted) return { tone: 'ok', text: outcome.landing.state === 'deferred' ? 'Approved: the landing waits behind work already queued.' : 'Approved: the landing goes on.' }
  const waiting = outcome.landing.waiting_for
  if (outcome.landing.state === 'finished') {
    return { tone: 'warn', text: 'Not accepted: the subject changed before your approval reached the landing, which has ended.' }
  }
  const differs = waiting?.for === 'approval' && waiting.stale ? ` (its ${partsNamed(waiting.stale.differs)})` : ''
  return { tone: 'warn', text: `Not accepted: the subject changed${differs} before your approval reached the landing. The new subject is below.` }
}

const STEP_NAMES: Record<ProposalStep, string> = {
  head: 'Head to GitHub',
  check: 'Targeted check',
  review: 'In review',
  queued: 'Queued',
  landing: 'Landing',
}

export function stepName(step: ProposalStep | null): string {
  return step ? (STEP_NAMES[step] ?? step) : 'Ended'
}

/**
 * What a running landing waits for. The server says GitHub (`forge`) for
 * reading main, moving it and reading it back alike, so the page does too.
 */
export function waitingName(landing: Landing): string {
  if (landing.state === 'deferred') return landing.waiting_for?.for === 'approval' ? 'Deferred: waiting for approval; other work can land' : 'Approved: waiting for its turn'
  if (landing.state === 'waiting') return 'Waiting for another landing'
  const waiting = landing.waiting_for
  switch (waiting?.for) {
    case 'forge':
      return 'Reading or moving main'
    case 'candidate':
      return waiting.build ? 'Building the candidate' : 'The candidate'
    case 'full_check':
      return waiting.base ? 'Landing check (affected parts)' : 'Full check'
    case 'approval':
      return 'Waiting for approval'
    default:
      return 'Finishing'
  }
}

const PART_NAMES: Record<SubjectPart, string> = {
  commit: 'commit',
  tree: 'tree',
  proposals: 'proposals',
  policy_version: 'policy version',
  evidence: 'evidence',
}

/** The parts of a subject, in words: `commit, tree`. */
export function partsNamed(parts: readonly SubjectPart[]): string {
  return parts.map((part) => PART_NAMES[part] ?? part).join(', ')
}

/** Why the policy reserves the landing for a person. */
export function reasonText(reason: PersonReason): string {
  switch (reason.reason) {
    case 'marked_path':
      return `It changes ${reason.path}, a path the policy marks.`
    case 'check_definition_changed':
      return `It changes ${reason.path}, a check definition.`
    case 'checks_changed':
      return `Its check definition differs from main's (${short(reason.from)} → ${short(reason.to)}).`
  }
}

/** The anchor of a proposal on the Board: its card under Ready to merge, or its approval's strip. */
export function proposalAnchor(id: string): string {
  return `proposal-${id}`
}

/** A link to a proposal on its project's Board (`key`), under Ready to merge, which scrolls to it and opens it while it is ready or asks for an approval. */
export function proposalPath(id: string, key?: string): string {
  return `${key ? projectPath(key, '/board') : '/board'}?live=ready#${proposalAnchor(id)}`
}

/** One issue a proposal carries, and the issue itself when the page holds it. */
export interface Carried {
  id: string
  issue: Issue | undefined
}

/**
 * Every issue a proposal carries, in the server's order, its first issue
 * first (Poulpe-456). One the page does not hold yet is kept, without its
 * issue: it is never dropped.
 */
export function carried(proposal: Pick<Proposal, 'issue_id' | 'issue_ids'>, issues: ReadonlyMap<string, Issue>): Carried[] {
  const ids = proposal.issue_ids.length > 0 ? proposal.issue_ids : [proposal.issue_id]
  return ids.map((id) => ({ id, issue: issues.get(id) }))
}

/** The keys of every issue a proposal carries, in order; its id for one the page does not hold. */
export function carriedKeys(proposal: Pick<Proposal, 'issue_id' | 'issue_ids'>, issues: ReadonlyMap<string, Issue>): string[] {
  return carried(proposal, issues).map(({ id, issue }) => issue?.key ?? id)
}

/** A commit, a tree or a hash, short. */
export function short(sha: string): string {
  return sha.slice(0, 7)
}

/** What to tell the person of an approval that was not sent, or not taken. */
export function problemText(outcome: Extract<ApprovalOutcome, { ok: false }>): string {
  switch (outcome.problem) {
    case 'mismatch':
      return 'Nothing was sent: the subject shown is not the one the server asks to approve. Reload the page and look again.'
    case 'identity_changed':
      return 'The identity changed; review again.'
    case 'cancelled':
      return 'No passkey was given, so nothing was approved.'
    case 'unsupported':
      return inMacShell() ? 'This Mac has no passkeys: approve from one that has.' : 'This browser has no passkeys: approve from one that has.'
    case 'refused':
      return 'The passkey was refused, so nothing was approved. Try again.'
    case 'failed':
      return `Could not approve: ${outcome.message}`
  }
}
